import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { useNoIndexMeta } from '@shared/hooks';
import { detectInAppBrowser } from '@shared/utils/inAppBrowser';
import {
  AddToCalendarView,
  type AddToCalendarBranch,
  type CalendarTarget,
} from '@features/booking/components/AddToCalendarView';
import { getAddToCalendarData } from '../api/reservations';
import type { AddToCalendarData } from '../api/reservations';
import {
  RESERVATION_EVENT_DURATION_MINUTES,
  amPmToHm,
  buildGoogleCalendarUrl,
  buildIcs,
  buildOutlookUrl,
  usDateToIso,
  zonedWallClockToUtc,
  type CalendarEvent,
} from '../utils/calendarLinks';
import { downloadIcs } from '../utils/downloadIcs';

/**
 * Which calendar the gold slot points at.
 *
 * The three options are peers — the right one is whichever calendar the guest
 * already uses — so rather than funnel everyone at Google we promote the
 * platform's own calendar and leave the other two equally reachable right
 * below. Apple devices get the .ics, which Apple Calendar owns; everything
 * else gets Google.
 *
 * iPadOS 13+ reports a Macintosh UA, so matching `Macintosh` is what catches
 * modern iPads — and a Mac is an Apple-calendar machine either way.
 */
function resolvePrimaryTarget(): CalendarTarget {
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod|Macintosh/i.test(ua) ? 'ics' : 'google';
}

/**
 * Open an external calendar.
 *
 * This page is reached from an email, so much of the traffic arrives in the
 * Gmail/Outlook webview, where `window.open` is routinely blocked while a
 * same-tab navigation still works. There is nothing to come back to on this
 * page, so navigating away costs the guest nothing.
 */
function openCalendarUrl(url: string): void {
  if (detectInAppBrowser().isInAppBrowser) {
    window.location.href = url;
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Landing page for the `add-to-calendar` link in every reservation email.
 *
 * Owns the route param, the fetch and the calendar-link building;
 * `AddToCalendarView` owns everything the guest sees.
 */
const AddToCalendarPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('booking');

  // The URL carries a reservation token, so keep it out of search indexes.
  useNoIndexMeta();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['addToCalendar', id],
    queryFn: () => getAddToCalendarData(id!),
    enabled: !!id,
    retry: false,
    // No `silentOn`: the endpoint answers 200 even for an unknown id, so any
    // status error here is a real fault worth reporting rather than the
    // routine expired-link noise other token pages suppress.
    meta: { source: 'add-to-calendar-link' },
  });

  // `toCalendarEvent` runs during render on three strings the backend controls.
  // A blank or non-IANA `timezone` and a malformed `dateFormatted` both throw,
  // and the app mounts no error boundary around this route, so an unguarded
  // throw here turns the email link into a blank page. Folding it into the
  // `error` branch shows the guest what happened and keeps the venue's contact
  // route open.
  const event = useMemo(() => {
    if (!data) return null;
    try {
      return toCalendarEvent(data, t);
    } catch {
      return null;
    }
  }, [data, t]);

  const primaryTarget = useMemo(resolvePrimaryTarget, []);

  // `data === null` is a found-nothing answer, not a pending one: the endpoint
  // returns 200 `[]` for an unknown id and the API layer maps that to null.
  const branch: AddToCalendarBranch = !id || isError
    ? 'error'
    : isLoading
      ? 'loading'
      : !data
        ? 'not-found'
        : !event
          ? 'error'
          : 'ready';

  const handleSelect = (target: CalendarTarget) => {
    if (!event || !id) return;
    if (target === 'google') return openCalendarUrl(buildGoogleCalendarUrl(event));
    if (target === 'outlook') return openCalendarUrl(buildOutlookUrl(event));
    downloadIcs(buildIcs(event, id), 'reserva.ics');
  };

  return (
    <AddToCalendarView
      branch={branch}
      // Only on `ready`: the view formats the date itself, so handing it a
      // payload we already failed to parse would just move the throw.
      reservation={branch === 'ready' ? data : null}
      primaryTarget={primaryTarget}
      onSelect={handleSelect}
      onGoHome={() => navigate('/')}
    />
  );
};

function toCalendarEvent(data: AddToCalendarData, t: TFunction): CalendarEvent {
  const start = zonedWallClockToUtc(
    usDateToIso(data.dateFormatted),
    amPmToHm(data.time),
    data.timezone,
  );
  const end = new Date(start.getTime() + RESERVATION_EVENT_DURATION_MINUTES * 60_000);

  return {
    title: `${t('ADD_TO_CALENDAR_EVENT_TITLE')} ${data.restaurantName}`,
    description: t('ADD_TO_CALENDAR_EVENT_DESCRIPTION', { partySize: data.partySize }),
    location: data.restaurantAddress,
    organizerName: data.restaurantName,
    organizerEmail: data.organizerEmail,
    start,
    end,
  };
}

export default AddToCalendarPage;
